/**
 * AI 응답 마크다운 → HTML 변환 (화면 무관 프리미티브)
 *
 * 소비처: Agent Chat 말풍선(MdBubble) / 시나리오 플래닝 인라인 텍스트(AiMdText) / 의사결정 리포트 HTML.
 * 어느 한쪽 소유가 아니므로 중립 leaf 로 둔다 — 이 파일은 pages/** 를 import 하지 않는다.
 */

// 파이프 표 구분행 판정 — 스캐너와 소비처(스트리밍 중 미완성 표 숨김)가 같은 판정을 쓴다.
export const MD_TABLE_SEP_RE = /^\s*\|?[\s:|-]*-{2,}[\s:|-]*\|?\s*$/;

// 파이프 마크다운 표 스캐너 — 원문을 md 조각과 표 구조로 가른다.
// tableToHtml(HTML 소비처)과 Agent Chat 말풍선(폭 맞춤 표 소비처)이 이 하나의 판정을
// 공유해야 한다 — 판정이 갈리면 같은 글이 화면마다 표가 되기도, 안 되기도 한다.
// md 조각은 줄 덩어리째 내보낸다(빈 줄 하나짜리 text:"" 조각도 내보내야 재조합이 원문과 일치).
export function splitMdTables(src) {
  const lines = src.split("\n");
  const isRow = (l) => l.includes("|");
  const isSep = (l) => MD_TABLE_SEP_RE.test(l);
  const cells = (l) => l.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim());
  const out = [];
  let buf = [];
  const flush = () => { if (buf.length) { out.push({ type: "md", text: buf.join("\n") }); buf = []; } };
  let i = 0;
  while (i < lines.length) {
    // 헤더행 다음 줄이 구분행이면 테이블로 인식
    if (isRow(lines[i]) && i + 1 < lines.length && isSep(lines[i + 1])) {
      flush();
      const header = cells(lines[i]);
      i += 2;
      const rows = [];
      while (i < lines.length && isRow(lines[i]) && !isSep(lines[i])) {
        rows.push(cells(lines[i]));
        i += 1;
      }
      out.push({ type: "table", header, rows });
    } else {
      buf.push(lines[i]);
      i += 1;
    }
  }
  flush();
  return out;
}

// 파이프 마크다운 테이블 → <table> (splitMdTables 위에 재구현 — 출력은 종전과 동일)
// 이스케이프(&<>) 직후, 줄바꿈→<br/> 치환 전에 호출해야 한다.
function tableToHtml(src) {
  return splitMdTables(src)
    .map((seg) => {
      if (seg.type === "md") return seg.text;
      const th = seg.header.map((c) => `<th>${c}</th>`).join("");
      const body = seg.rows
        .map((r) => `<tr>${r.map((c) => `<td>${c}</td>`).join("")}</tr>`)
        .join("");
      return `<table class="md-table"><thead><tr>${th}</tr></thead><tbody>${body}</tbody></table>`;
    })
    .join("\n");
}

// 간이 마크다운 → HTML
export function mdToHtml(src) {
  if (!src) return "";
  let s = src.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  s = tableToHtml(s);   // 파이프 테이블 먼저 변환 (줄바꿈 치환 전)
  return s
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/\*\*([^*\n]+)\*\*/g, "<strong>$1</strong>")
    .replace(/\*([^*\n]+)\*/g, "<em>$1</em>")
    .replace(/^#{1,4}\s+(.+)$/gm, "<strong class='md-h'>$1</strong>")
    .replace(/^-{3,}$/gm, "<hr/>")
    .replace(/^(\d+)\.\s+(.+)$/gm, "<li class='md-ol'>$2</li>")
    .replace(/^[-•]\s+(.+)$/gm, "<li>$1</li>")
    // 불릿 사이 빈 줄 제거 → 인접 항목을 한 목록으로 묶어 과도한 간격 방지
    .replace(/<\/li>\s*\n\s*\n\s*(?=<li)/g, "</li>\n")
    .replace(/(<li(?:\s[^>]*)?>[\s\S]*?<\/li>\n?)+/g, (m) => {
      const isOl = m.includes("class='md-ol'");
      // 항목 사이 줄바꿈 제거 — 안 그러면 마지막 \n→<br/> 치환으로 <li> 사이에 빈 줄이 생김
      const items = m.replace(/\n+/g, "").replace(/ class='md-ol'/g, "");
      return isOl
        ? `<ol style="padding-left:16px;margin:4px 0">${items}</ol>`
        : `<ul style="padding-left:16px;margin:4px 0">${items}</ul>`;
    })
    .replace(/\n\n+/g, "<br/><br/>")
    .replace(/\n/g, "<br/>")
    // ### 소제목(.md-h)은 block 이라 뒤따르는 <br/> 가 큰 공백을 만듦 → 제거
    .replace(/(<strong class='md-h'>[\s\S]*?<\/strong>)(?:<br\/>)+/g, "$1");
}

// 인라인 마크다운 기호 제거 — 선택지 패널처럼 굵게·코드 스타일을 쓰지 않는 UI 크롬용.
//   백엔드 parse_choices 는 LLM 이 쓴 prompt·label 을 검증만 하고 통과시키므로 `**` 가 섞여 올 수 있다.
//   말풍선(mdToHtml)과 달리 여기서는 스타일 없이 평문만 남기고 개행도 공백으로 접는다.
export function stripMdInline(s) {
  if (s == null || s === "") return "";
  return String(s)
    .replace(/`([^`]*)`/g, "$1")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/(^|[^*])\*([^*\n]+)\*/g, "$1$2")
    .replace(/^\s{0,3}#{1,6}\s+/gm, "")
    .replace(/^\s*[-•]\s+/gm, "")
    .replace(/\s*\n+\s*/g, " ")
    .trim();
}
